import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

const useCourseUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersCollection = collection(db, 'users');
        const snapshot = await getDocs(usersCollection);
        
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setUsers(usersData);
        setError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Separate users into potential authors and students
  const { authors, students, allUsers } = useMemo(() => {
    const isAuthorRole = (user) => {
      if (!user.role) return false;
      
      // Handle role as array (e.g., ["instructor"]) or string
      const roles = Array.isArray(user.role) ? user.role : [user.role];
      
      return roles.some(role => 
        role === 'instructor' || 
        role === 'admin' || 
        role === 'teacher' ||
        role === 'author'
      );
    };
    
    const isStudentRole = (user) => {
      if (!user.role) return true; // Default to student if no role
      
      // Handle role as array (e.g., ["student"]) or string
      const roles = Array.isArray(user.role) ? user.role : [user.role];
      
      return roles.some(role => 
        role === 'student' || 
        role === 'user' ||
        role === ''
      ) || !roles.some(role => ['instructor', 'admin', 'teacher', 'author'].includes(role));
    };
    
    const authors = users.filter(isAuthorRole);
    const students = users.filter(isStudentRole);
    
    // If no authors found with specific roles, show all users as potential authors
    // This allows assignment even if roles aren't properly set up
    const finalAuthors = authors.length > 0 ? authors : users;
    
    return {
      authors: finalAuthors,
      students,
      allUsers: users
    };
  }, [users]);

  const getUserById = (userId) => {
    return users.find(user => user.id === userId);
  };

  const getUsersByIds = (userIds) => {
    if (!Array.isArray(userIds)) return [];
    return users.filter(user => userIds.includes(user.id));
  };

  return {
    users: allUsers,
    authors,
    students,
    loading,
    error,
    getUserById,
    getUsersByIds
  };
};

export default useCourseUsers;
